import { useCallback, useEffect, useState } from "react";

import type { StoryFlowLockDiagnostics } from "../types/storyFlowLocks";

import {
  listFlowLockDiagnosticsAsUser,
  releaseFlowLockAsUser,
} from "../dashboard-client";
import { parseLockDiagnostics } from "../utils/storyFlowLocks";
import { useDashboardRealtime } from "./useDashboardRealtime";

export function useStoryFlowLocks() {
  const [diagnostics, setDiagnostics] =
    useState<StoryFlowLockDiagnostics | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [clearingKey, setClearingKey] = useState<string | null>(null);
  const { subscribe } = useDashboardRealtime();

  const fetchLocks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listFlowLockDiagnosticsAsUser();
      setDiagnostics(parseLockDiagnostics(data));
      setFetchError(null);
    } catch (error) {
      setDiagnostics(null);
      setFetchError(
        error instanceof Error
          ? error.message
          : "Failed to load lock diagnostics.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchLocks();
  }, [fetchLocks]);

  useEffect(() => {
    let timeout: number | null = null;
    const scheduleRefresh = () => {
      if (timeout !== null) {
        return;
      }
      timeout = window.setTimeout(() => {
        timeout = null;
        fetchLocks().catch(() => {
          /* Handled in fetchLocks */
        });
      }, 500);
    };

    const unsubscribe = subscribe(
      (message) => {
        const { type } = message;
        if (
          type === "compile_recording_snapshot" ||
          type === "execution_request_snapshot" ||
          type === "execution_node_snapshot" ||
          type === "plan_revision_updated"
        ) {
          scheduleRefresh();
        }
      },
      { topic: "internalEventTopic" },
    );

    return () => {
      unsubscribe();
      if (timeout !== null) {
        window.clearTimeout(timeout);
        timeout = null;
      }
    };
  }, [fetchLocks, subscribe]);

  const clearLock = useCallback(
    async (storyIssueKey: string) => {
      if (!storyIssueKey) {
        return;
      }
      setClearingKey(storyIssueKey);
      try {
        await releaseFlowLockAsUser({ path: { storyIssueKey } });
        await fetchLocks();
      } catch (error) {
        setFetchError(
          error instanceof Error ? error.message : "Failed to clear lock.",
        );
      } finally {
        setClearingKey(null);
      }
    },
    [fetchLocks],
  );

  return {
    clearLock,
    clearingKey,
    diagnostics,
    error: fetchError,
    loading,
    refresh: fetchLocks,
  };
}
